"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { keepPreviousData, type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiWaveOutcomeDistributionItemsPage } from "@/generated/models/ApiWaveOutcomeDistributionItemsPage";
import { commonApiFetch } from "@/services/api/common-api";
type WaveOutcomeDistributionSortDirection = "ASC" | "DESC";
interface UseWaveOutcomeDistributionQueryParams {
  readonly waveId?: string | null | undefined;
  readonly outcomeIndex?: string | number | null | undefined;
  readonly pageSize?: number | undefined;
  readonly sortDirection?: WaveOutcomeDistributionSortDirection | undefined;
  readonly enabled?: boolean | undefined;
}
type WaveOutcomeDistributionInfiniteData = InfiniteData<ApiWaveOutcomeDistributionItemsPage>;
type UseWaveOutcomeDistributionQueryResult = UseInfiniteQueryResult<WaveOutcomeDistributionInfiniteData, Error> & {
  readonly items: ApiWaveOutcomeDistributionItemsPage["data"];
  readonly totalCount: number;
  readonly errorMessage?: string | undefined;
  readonly isEnabled: boolean;
};
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_SORT_DIRECTION: WaveOutcomeDistributionSortDirection = "ASC";
const DEFAULT_STALE_TIME = 30_000;
export function useWaveOutcomeDistributionQuery(t0) {
  const $ = _c(29);
  const {
    waveId,
    outcomeIndex,
    pageSize: t1,
    sortDirection: t2,
    enabled: t3
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_PAGE_SIZE : t1;
  const sortDirection = t2 === undefined ? DEFAULT_SORT_DIRECTION : t2;
  const enabled = t3 === undefined ? true : t3;
  let t4;
  if ($[0] !== waveId) {
    t4 = waveId?.trim() ?? "";
    $[0] = waveId;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const normalizedWaveId = t4;
  let t5;
  if ($[2] !== outcomeIndex) {
    t5 = outcomeIndex === null ? "" : String(outcomeIndex).trim();
    $[2] = outcomeIndex;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const normalizedOutcomeIndex = t5;
  const hasRequiredParams = Boolean(normalizedWaveId) && Boolean(normalizedOutcomeIndex);
  const isEnabled = hasRequiredParams && enabled;
  let t6;
  if ($[4] !== pageSize) {
    t6 = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : DEFAULT_PAGE_SIZE;
    $[4] = pageSize;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const normalizedPageSize = t6;
  const normalizedSortDirection = sortDirection === "DESC" ? "DESC" : "ASC";
  let t7;
  if ($[6] !== normalizedOutcomeIndex || $[7] !== normalizedPageSize || $[8] !== normalizedSortDirection || $[9] !== normalizedWaveId) {
    t7 = [QueryKey.WAVE_OUTCOME_DISTRIBUTION, normalizedWaveId, normalizedOutcomeIndex, normalizedPageSize, normalizedSortDirection];
    $[6] = normalizedOutcomeIndex;
    $[7] = normalizedPageSize;
    $[8] = normalizedSortDirection;
    $[9] = normalizedWaveId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const queryKey = t7;
  let t8;
  if ($[11] !== normalizedOutcomeIndex || $[12] !== normalizedPageSize || $[13] !== normalizedSortDirection || $[14] !== normalizedWaveId) {
    t8 = async t9 => {
      const {
        pageParam
      } = t9;
      const currentPage = pageParam ?? DEFAULT_PAGE;
      const params = {
        page: currentPage.toString(),
        page_size: normalizedPageSize.toString(),
        sort_direction: normalizedSortDirection
      };
      return commonApiFetch({
        endpoint: `waves/${normalizedWaveId}/outcomes/${normalizedOutcomeIndex}/distribution`,
        params
      });
    };
    $[11] = normalizedOutcomeIndex;
    $[12] = normalizedPageSize;
    $[13] = normalizedSortDirection;
    $[14] = normalizedWaveId;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = getDefaultQueryRetry();
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isEnabled || $[18] !== queryKey || $[19] !== t8) {
    t10 = {
      queryKey,
      queryFn: t8,
      initialPageParam: DEFAULT_PAGE,
      getNextPageParam: _temp,
      enabled: isEnabled,
      staleTime: DEFAULT_STALE_TIME,
      placeholderData: keepPreviousData,
      ...t9
    };
    $[17] = isEnabled;
    $[18] = queryKey;
    $[19] = t8;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const query = useInfiniteQuery(t10);
  let t11;
  if ($[21] !== query.data?.pages) {
    t11 = query.data?.pages.flatMap(_temp2) ?? [];
    $[21] = query.data?.pages;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const items = t11;
  const totalCount = query.data?.pages[0]?.count ?? 0;
  const errorMessage = query.error instanceof Error ? query.error.message : undefined;
  let t12;
  if ($[23] !== errorMessage || $[24] !== isEnabled || $[25] !== items || $[26] !== query || $[27] !== totalCount) {
    t12 = {
      ...query,
      items,
      totalCount,
      errorMessage,
      isEnabled
    };
    $[23] = errorMessage;
    $[24] = isEnabled;
    $[25] = items;
    $[26] = query;
    $[27] = totalCount;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}
function _temp2(pageData) {
  return pageData.data;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}